import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { gettext } from '@eventbrite/i18n';
import { IMAGE_TYPE_EVENT_LOGO } from '@eventbrite/http';
import { connectImageField } from '@eventbrite/redux-image-field';

import { Layout } from '@eventbrite/eds-layout';
import { Divider } from '@eventbrite/eds-divider';
import { AlertChunky } from '@eventbrite/eds-iconography';
import { _ImageField } from '@eventbrite/eds-image-field';
import { TYPE_ERROR } from '@eventbrite/eds-notification';
import { CROP_REQUIRED } from '@eventbrite/eds-image-field';
import { collectionsDuck } from '@eventbrite/redux-destination';

import { EventContainer } from '../containers/EventContainer';

import PageHeaderEdit from '../components/PageHeaderEdit';
import CollectionSummary from '../components/CollectionSummary';
import PageHeader from '../components/PageHeader';
import ConditionalComponent from '../components/ConditionalComponent';
import OwnerActionBar from '../components/OwnerActionBar';
import FollowCTA from '../components/FollowCTA';
import { CollectionPageBreadCrumb } from '../containers/CollectionPageBreadCrumb/CollectionPageBreadCrumb';

import './collectionPage.scss';

const {
    constants: { COLLECTION_PROP_TYPE },
} = collectionsDuck;
const CROP_2_TO_1 = 2;
const ImageField = connectImageField(_ImageField, 'collection', 'imageLarge');

const DivPageWrapper = ({ children }) => <div>{children}</div>;

const FormPageWrapper = ({ children, _handleSubmit }) => (
    <form onSubmit={_handleSubmit} data-spec="event-section-form">
        {children}
    </form>
);

const HeaderBackground = ({
    collectionImageUrl,
    collectionName,
    collectionImageArtistAttribution,
    isEditMode,
    handleImageClick,
}) => {
    if (isEditMode) {
        return (
            <div
                className="
                eds-l-sn-mar-top-8
                eds-l-sm-mar-top-8
                eds-l-sw-mar-top-8
                eds-l-mn-mar-left-12
                eds-l-md-mar-left-12
                eds-l-mw-mar-left-12
                eds-l-ln-mar-left-12
                eds-l-lg-mar-left-12
                eds-l-lw-mar-left-12
            "
            >
                <div className="user-collection__header-image eds-align--center">
                    <ImageField
                        imageType={IMAGE_TYPE_EVENT_LOGO}
                        imageAlt="Collection image"
                        hiddenLabel="Collection image"
                        cropIntention={CROP_REQUIRED}
                        cropAspectRatio={CROP_2_TO_1}
                        onReceiveFile={handleImageClick}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="user-collection__header-image-container-extra">
            <div
                className="user-collection__header-image eds-show-up-mn"
                style={{
                    backgroundImage: `url(${collectionImageUrl})`,
                }}
            />
            <img
                className="user-collection__header-image eds-show-down-sw"
                src={collectionImageUrl}
                alt={collectionName}
            />
            {collectionImageArtistAttribution && (
                <p
                    className="
            user-collection__header-image-artist-attribution
            eds-text-bs
            eds-text-color--white
        "
                >
                    {collectionImageArtistAttribution}
                </p>
            )}
        </div>
    );
};

export default class CollectionPage extends Component {
    static propTypes = {
        isLoading: PropTypes.bool,
        events: PropTypes.arrayOf(PropTypes.object),
        isEventsLoading: PropTypes.bool,
        isOwner: PropTypes.bool,
        isQuality: PropTypes.bool,
        isFollowing: PropTypes.bool,
        userId: PropTypes.string,
        collectionName: PropTypes.string,
        collectionSummary: PropTypes.string,
        creatorName: PropTypes.string,
        creatorImage: PropTypes.string,
        shareUrl: PropTypes.string,
        handleCollectionEdit: PropTypes.func,
        onSaveEvent: PropTypes.func,
        handleViewEvent: PropTypes.func,
        hasUpcomingPagination: PropTypes.bool,
        handleSeeMoreEvents: PropTypes.func,
        userCollections: COLLECTION_PROP_TYPE,
        onAddOrRemoveEventToCollection: PropTypes.func,
        onCreateCollection: PropTypes.func,
        handleFollowCollection: PropTypes.func,
        handleUnfollowCollection: PropTypes.func,
        isPublic: PropTypes.bool,
        displayCreatorName: PropTypes.string,
        displayCreatorDescription: PropTypes.string,
        isEditMode: PropTypes.bool.isRequired,
        isGDPRCountry: PropTypes.bool.isRequired,
        isInfluencer: PropTypes.bool,
    };

    componentDidUpdate(prevProps) {
        const { hasError, addMainNotification, clearError } = this.props;
        const { hasError: prevHasError } = prevProps;

        if (!prevHasError && hasError) {
            addMainNotification({
                children: gettext(
                    'We encountered a server error. Please try again.',
                ),
                onClose: clearError,
                hasCloseButton: true,
                isNotificationSticky: true,
                iconType: <AlertChunky />,
                type: TYPE_ERROR,
            });
        }
    }

    render() {
        const {
            isOwner,
            collectionId,
            collectionName,
            collectionSummary,
            collectionImageUrl,
            collectionImageArtistAttribution,
            creatorName,
            creatorImage,
            location,
            shareUrl,
            getShareUrl,
            userId,
            toggleEditMode,
            isEditMode,
            saveCollectionEdits,
            handleSubmit,
            trackEvent,
            displayCreatorName,
            displayCreatorDescription,
            isQuality,
            isPublic,
            handleFollowCollection,
            handleUnfollowCollection,
            isFollowing,
            invalid,
            submitting,
            pristine,
            reset,
            isGDPRCountry,
            isInfluencer,
        } = this.props;

        let imageCmp;
        let PageHeaderClass = PageHeader;
        let PageWrapper = DivPageWrapper;

        if (collectionImageUrl) {
            imageCmp = (
                <HeaderBackground
                    collectionImageUrl={collectionImageUrl}
                    collectionName={collectionName}
                    collectionImageArtistAttribution={
                        collectionImageArtistAttribution
                    }
                    isEditMode={isEditMode}
                    handleImageClick={this._handleImageClick}
                />
            );
        }

        if (isEditMode) {
            PageHeaderClass = PageHeaderEdit;
            PageWrapper = FormPageWrapper;
        }

        const ownerActionBarNode = (
            <ConditionalComponent shouldRender={isOwner}>
                <OwnerActionBar
                    collectionId={collectionId}
                    toggleEditMode={toggleEditMode}
                    shareUrl={shareUrl}
                    getShareUrl={getShareUrl}
                    userId={userId}
                    isEditMode={isEditMode}
                    saveCollectionEdits={saveCollectionEdits}
                    isSaveDisabled={pristine || submitting || invalid}
                    trackEvent={trackEvent}
                    onCancel={reset}
                    isEditorial={isQuality}
                />
            </ConditionalComponent>
        );

        // For blur effect on desktop
        const imageMask = (
            <div
                className="
                    user-collection__header-image-mask-container
                    eds-l-ln-mar-hor-6
                    eds-l-lg-mar-hor-6
                    eds-l-lw-mar-hor-6
                "
            >
                <img
                    fetchpriority="high"
                    className="user-collection__header-image-mask"
                    src={collectionImageUrl}
                    alt="blur-decoration"
                    loading="eager"
                />
            </div>
        );

        const followCTANode = (
            <ConditionalComponent shouldRender={isQuality && !isEditMode}>
                <FollowCTA
                    handleFollowCollection={handleFollowCollection}
                    handleUnfollowCollection={handleUnfollowCollection}
                    isFollowing={isFollowing}
                    isPublic={isPublic}
                    trackEvent={trackEvent}
                    userId={userId}
                    location={location}
                    collectionId={collectionId}
                    collectionName={collectionName}
                    collectionImage={collectionImageUrl}
                    isGDPRCountry={isGDPRCountry}
                />
            </ConditionalComponent>
        );

        const pageHeaderCmp = (
            <PageHeaderClass
                collectionName={collectionName}
                collectionSummary={collectionSummary}
                creatorName={creatorName}
                creatorImage={creatorImage}
                displayCreatorName={displayCreatorName}
                displayCreatorDescription={displayCreatorDescription}
                isQuality={isQuality}
                isEditMode={isEditMode}
                collectionImageUrl={collectionImageUrl}
                handleFollowCollection={handleFollowCollection}
                handleUnfollowCollection={handleUnfollowCollection}
                isFollowing={isFollowing}
                isPublic={isPublic}
                followCTANode={followCTANode}
                ownerActionBarNode={ownerActionBarNode}
                isOwner={isOwner}
                collectionId={collectionId}
                isInfluencer={isInfluencer}
            />
        );

        let headerOnlyDesktop;
        let headerAndImageDesktop;

        if (imageCmp) {
            headerAndImageDesktop = (
                <div className="user-collection__header-image-container">
                    {pageHeaderCmp}
                    {imageCmp}
                </div>
            );
        }

        if (!headerAndImageDesktop) {
            headerOnlyDesktop = (
                <div className="user-collection__header-only-container eds-l-mar-top-6">
                    {pageHeaderCmp}
                </div>
            );
        }

        // Use of double layout is to allow the image to go past the layout max-width
        const desktopLayout = (
            <main className="eds-show-up-mn eds-l-ln-mar-top-12 eds-l-lg-mar-top-12 eds-l-lw-mar-top-12">
                <section>
                    {!isEditMode && collectionImageUrl && imageMask}
                    <div>
                        {headerAndImageDesktop}
                        {headerOnlyDesktop && (
                            <Layout
                                maxWidth="large"
                                hasHorizontalGutters={true}
                            >
                                {headerOnlyDesktop}
                            </Layout>
                        )}
                        <div
                            className={classnames(
                                { 'eds-l-mar-top-8': collectionImageUrl },
                                { 'eds-l-mar-top-16': !collectionImageUrl },
                            )}
                        >
                            <Layout
                                maxWidth="large"
                                hasHorizontalGutters={true}
                            >
                                <div className="user-collection__events-section--featured-organizers">
                                    <EventContainer />
                                </div>
                            </Layout>
                        </div>
                    </div>
                </section>
            </main>
        );

        const mobileLayout = (
            <main className="eds-show-down-sw">
                {imageCmp}
                <Layout maxWidth="large" hasHorizontalGutters={true}>
                    {pageHeaderCmp}
                    <div className="eds-show-down-sw">
                        {isInfluencer && (
                            <ConditionalComponent
                                shouldRender={collectionSummary}
                            >
                                <div className="eds-l-mar-top-2 eds-l-mar-bot-8">
                                    <CollectionSummary
                                        collectionSummary={collectionSummary}
                                        isEditMode={isEditMode}
                                    />
                                </div>
                            </ConditionalComponent>
                        )}
                        {isOwner && !isEditMode
                            ? ownerActionBarNode
                            : followCTANode}
                        {!isInfluencer && (
                            <ConditionalComponent
                                shouldRender={collectionSummary}
                            >
                                <div className="eds-l-mar-top-2 eds-l-mar-bot-8">
                                    <CollectionSummary
                                        collectionSummary={collectionSummary}
                                        isEditMode={isEditMode}
                                    />
                                </div>
                            </ConditionalComponent>
                        )}
                        <Divider color="grey-200" />
                        <EventContainer />
                    </div>
                </Layout>
            </main>
        );

        return (
            <PageWrapper _handleSubmit={handleSubmit}>
                <CollectionPageBreadCrumb
                    collectionName={collectionName}
                    collectionId={collectionId}
                />
                {desktopLayout}
                {mobileLayout}
            </PageWrapper>
        );
    }
}
